import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Chip, IconButton, Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Groups, Person, Assignment,
  BarChart, WorkOutline, Home, Refresh, Business, TrendingUp, Groups2, BeachAccess, Gavel, Logout
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const PLANNED = [
  { icon: WorkOutline, label: 'Contracts',  desc: 'Employment contracts and terms'    },
  { icon: BarChart,    label: 'HR Reports', desc: 'Headcount, turnover and analytics' },
];

const HRDashboard = () => {
  const navigate  = useNavigate();
  const [counts,  setCounts]  = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const { data } = await axios.get(`${API}/api/employees`, { headers: h });
      const active = data.filter(e => e.status === 'Active').length;
      setCounts({ total: data.length, active });
    } catch {
      setCounts({ total: 0, active: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2
        }}>
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <Groups sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={700}>Human Resources</Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Employee Management · Leave · Contracts · Appraisals
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton onClick={load} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Home">
              <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Activities cue */}
        <Box mb={3}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Activities</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={2} sx={{
                borderRadius: 3, cursor: 'pointer', borderLeft: '4px solid #546e7a',
                transition: 'transform .15s, box-shadow .15s',
                '&:hover': { boxShadow: 5, transform: 'translateY(-2px)' }
              }} onClick={() => navigate('/hr/employees')}>
                <CardContent sx={{ py: 1.8 }}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight={800} lineHeight={1} color="#546e7a">
                        {loading ? '…' : counts.active ?? 0}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} mt={0.5}>Active Employees</Typography>
                    </Box>
                    <Box sx={{
                      width: 42, height: 42, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: '#546e7a18'
                    }}>
                      <Person sx={{ fontSize: 21, color: '#546e7a' }} />
                    </Box>
                  </Box>
                  {counts.total > 0 && counts.total !== counts.active && (
                    <Typography variant="caption" color="text.secondary">
                      {counts.total} total · {counts.total - counts.active} inactive
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Live modules */}
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Modules</Typography>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #546e7a',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/employees')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{
                    width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: '#546e7a1A'
                  }}>
                    <Person sx={{ fontSize: 24, color: '#546e7a' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Employees</Typography>
                    <Chip label={loading ? '…' : counts.total ?? 0} size="small"
                      sx={{ mt: 0.5, height: 20, fontSize: 12, bgcolor: '#546e7a22', color: '#546e7a' }} />
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Employee records, contact details and employment status
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #37474f',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/departments')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#37474f1A' }}>
                    <Business sx={{ fontSize: 24, color: '#37474f' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Departments</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Manage organisational departments used on employee records
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #1565c0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/pay-grades')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#1565c01A' }}>
                    <TrendingUp sx={{ fontSize: 24, color: '#1565c0' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Pay Grades & Steps</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Salary grades (G1–M2) and pay steps for annual increments
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #2e7d32',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/pay-groups')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#2e7d321A' }}>
                    <Groups2 sx={{ fontSize: 24, color: '#2e7d32' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Pay Groups</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Payroll processing groups — frequency, GL account, payment bank
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #6a1b9a',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/appraisal')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#6a1b9a1A' }}>
                    <Assignment sx={{ fontSize: 24, color: '#6a1b9a' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Performance Appraisal</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Goal-setting, supervisor approval, self &amp; supervisor scoring, PIP and reports
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #c62828',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/disciplinary')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#c628281A' }}>
                    <Gavel sx={{ fontSize: 24, color: '#c62828' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Disciplinary</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Cases, progressive warnings, gross misconduct, suspension and reports
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #37474f',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/exit')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#37474f1A' }}>
                    <Logout sx={{ fontSize: 24, color: '#37474f' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Separation &amp; Exit</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Resignation, retirement, clearance, terminal dues, exit interview &amp; certificate
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: 3, cursor: 'pointer', borderTop: '4px solid #1565c0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
            }} onClick={() => navigate('/hr/leave')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#1565c01A' }}>
                    <BeachAccess sx={{ fontSize: 24, color: '#1565c0' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Leave Management</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.5}>
                      Leave applications, balances, allocation and carry forward
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Planned features */}
        <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="text.secondary">
          Planned Features
        </Typography>
        <Grid container spacing={2}>
          {PLANNED.map(({ icon: Icon, label, desc }) => (
            <Grid item xs={12} sm={6} md={4} key={label}>
              <Card elevation={0} sx={{
                borderRadius: 2, opacity: 0.5, bgcolor: '#f5f5f5',
                border: '1px dashed #ccc'
              }}>
                <CardContent sx={{ py: 1.5, px: 2 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ fontSize: 22, color: '#90a4ae' }} />
                    <Box>
                      <Typography variant="body2" fontWeight={700} color="text.disabled">{label}</Typography>
                      <Typography variant="caption" color="text.disabled">{desc}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Box>
    </Box>
  );
};

export default HRDashboard;
